import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, ChevronDown, User, KeyRound, LogOut, Loader2, Camera, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { MfaDialog } from "@/components/MfaDialog";
import { officerHasConsole, officerAllowedModules } from "@/lib/access";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { displayName } from "@/lib/format";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const ROLE_LABEL = { admin: "School Admin", corporate_admin: "Corporate Admin", officer: "Officer", staff: "Staff", teacher: "Teacher", driver: "Bus Driver", platform_owner: "Platform Owner" };
const inputCls = "w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/15";

export function Avatar({ user, size = "h-10 w-10", className = "" }) {
  const initials = (displayName(user?.name) || user?.email || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return user?.avatar_data_url ? (
    <img src={user.avatar_data_url} alt="" className={`${size} rounded-full object-cover ring-2 ring-white ${className}`} />
  ) : (
    <div className={`${size} rounded-full bg-slate-900 text-white flex items-center justify-center font-display font-bold text-sm ring-2 ring-white ${className}`}>{initials}</div>
  );
}

export default function UserMenu({ roleLabel, className = "" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [dlg, setDlg] = useState(null); // "profile" | "password"
  if (!user) return null;
  const doLogout = async () => { await logout(); nav("/login"); };
  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="user-menu-trigger"
            className="flex items-center gap-3 rounded-full bg-white/80 backdrop-blur border border-slate-200 pl-1.5 pr-3 py-1.5 text-left shadow-sm hover:bg-white hover:shadow transition outline-none focus-visible:ring-4 focus-visible:ring-primary/20">
            <Avatar user={user} />
            <div className="hidden sm:block min-w-0 max-w-[180px]">
              <div className="font-display text-sm font-bold leading-tight text-slate-900 truncate" data-testid="user-menu-name">{displayName(user.name)}</div>
              <div className="text-[11px] text-slate-500 truncate">{roleLabel || ROLE_LABEL[user.role] || user.role}</div>
            </div>
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} className="w-64 rounded-xl p-1.5 shadow-xl" data-testid="user-menu">
          {officerHasConsole(user) && (
            <DropdownMenuItem onClick={() => nav(officerAllowedModules(user)[0].path)} data-testid="user-menu-console" className="gap-3 rounded-lg px-3 py-2.5 text-[15px] cursor-pointer">
              <LayoutDashboard className="h-4 w-4 text-primary" /> My console
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setDlg("profile")} data-testid="user-menu-profile" className="gap-3 rounded-lg px-3 py-2.5 text-[15px] cursor-pointer">
            <User className="h-5 w-5 text-slate-500" /> My Profile
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDlg("password")} data-testid="user-menu-password" className="gap-3 rounded-lg px-3 py-2.5 text-[15px] cursor-pointer">
            <KeyRound className="h-5 w-5 text-slate-500" /> Reset Password
          </DropdownMenuItem>
          {user.role !== "parent" && (
            <DropdownMenuItem onClick={() => setDlg("mfa")} data-testid="user-menu-mfa" className="gap-3 rounded-lg px-3 py-2.5 text-[15px] cursor-pointer">
              <ShieldCheck className={`h-5 w-5 ${user.mfa_enabled ? "text-emerald-600" : "text-slate-500"}`} /> Two-factor authentication
              {user.mfa_enabled && <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">On</span>}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={doLogout} data-testid="user-menu-logout" className="gap-3 rounded-lg px-3 py-2.5 text-[15px] cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50">
            <LogOut className="h-5 w-5" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dlg === "profile" && <ProfileDialog onClose={() => setDlg(null)} roleLabel={roleLabel} />}
      {dlg === "password" && <ChangePasswordDialog onClose={() => setDlg(null)} />}
      {dlg === "mfa" && <MfaDialog onClose={() => setDlg(null)} />}
    </div>
  );
}

function resizeImage(file, max = 160) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

function ProfileDialog({ onClose, roleLabel }) {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [avatar, setAvatar] = useState(user.avatar_data_url || "");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try { setAvatar(await resizeImage(f)); } catch { toast.error("Could not read that image"); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me", { name, avatar_data_url: avatar });
      await refresh();
      toast.success("Profile updated");
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="profile-dialog">
        <DialogHeader><DialogTitle className="font-display">My Profile</DialogTitle><DialogDescription>Your name and photo are shown to colleagues and parents.</DialogDescription></DialogHeader>
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => fileRef.current?.click()} className="relative group" data-testid="profile-avatar-btn" title="Change photo">
            <Avatar user={{ ...user, name, avatar_data_url: avatar }} size="h-20 w-20" className="text-xl" />
            <span className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white"><Camera className="h-6 w-6" /></span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="profile-avatar-input" />
          <div className="text-sm text-slate-600">
            <button type="button" onClick={() => fileRef.current?.click()} className="text-primary font-medium hover:underline">Upload photo</button>
            {avatar && <> · <button type="button" onClick={() => setAvatar("")} className="text-slate-500 hover:underline" data-testid="profile-avatar-remove">Remove</button></>}
            <div className="text-xs text-slate-400 mt-1">JPG or PNG, auto-resized</div>
          </div>
        </div>
        <label className="block"><span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">Full name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" className={inputCls} /></label>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Email</div><div className="text-slate-900 truncate" data-testid="profile-email">{user.email || "—"}</div></div>
          <div><div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Role</div><div className="text-slate-900" data-testid="profile-role">{roleLabel || ROLE_LABEL[user.role] || user.role}</div></div>
        </div>
        <DialogFooter>
          <button onClick={save} disabled={saving || name.trim().length < 2} data-testid="profile-save-btn"
            className="h-10 rounded-lg bg-primary px-5 text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangePasswordDialog({ onClose }) {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const mismatch = f.confirm && f.confirm !== f.new_password;

  const save = async (e) => {
    e.preventDefault();
    if (f.new_password.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (mismatch) { toast.error("Passwords don't match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password changed");
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password"); }
    finally { setSaving(false); }
  };
  const T = show ? "text" : "password";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="font-display">Reset Password</DialogTitle><DialogDescription>Enter your current password, then choose a new one (min. 8 characters).</DialogDescription></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <input type={T} value={f.current_password} onChange={set("current_password")} placeholder="Current password" required autoComplete="current-password" data-testid="pw-current" className={inputCls} />
          <input type={T} value={f.new_password} onChange={set("new_password")} placeholder="New password" required minLength={8} autoComplete="new-password" data-testid="pw-new" className={inputCls} />
          <input type={T} value={f.confirm} onChange={set("confirm")} placeholder="Confirm new password" required autoComplete="new-password" data-testid="pw-confirm"
            className={`${inputCls} ${mismatch ? "border-red-400" : ""}`} />
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => setShow((s) => !s)} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900" data-testid="pw-toggle">
              {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {show ? "Hide" : "Show"} passwords
            </button>
            {mismatch && <span className="text-xs text-red-600" data-testid="pw-mismatch">Passwords don't match</span>}
          </div>
          <DialogFooter>
            <button type="submit" disabled={saving} data-testid="pw-save-btn" className="h-10 rounded-lg bg-primary px-5 text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Update password
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
